/**
 * CommunityMessaging - Platform-mediated messaging for community posts
 * Allows users to contact post owners without exposing personal contact info
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  MessageSquare, Send, ArrowLeft, Loader2, CheckCircle, 
  AlertTriangle, User, Mail, Phone, MapPin, Calendar,
  Inbox, SendHorizontal, Eye, Clock, MoreVertical
} from 'lucide-react';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Contact Form Component
const ContactPostOwnerForm = ({ post, onSuccess }) => {
  const [formData, setFormData] = useState({
    sender_name: '',
    sender_email: '',
    sender_phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.sender_name || !formData.sender_email || !formData.message) {
      setError('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/community/posts/${post.id}/contact?country=${post.country || 'LK'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          post_id: post.id,
          ...formData
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send message');
      }
      
      setSuccess(true);
      if (onSuccess) onSuccess();
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (success) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold mb-2">Message Sent!</h3>
        <p className="text-gray-600 mb-4">
          Your message has been sent to the post owner. They will contact you via your provided email.
        </p>
        <Button onClick={() => setSuccess(false)} variant="outline">
          Send Another Message
        </Button>
      </div>
    );
  }
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Post Preview */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <p className="text-sm text-gray-500 mb-1">Contacting about:</p>
        <h4 className="font-semibold">{post.title}</h4>
        <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
          <MapPin className="w-3 h-3" />
          {post.location}
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Your Name *</Label>
          <Input 
            placeholder="Full name"
            value={formData.sender_name}
            onChange={(e) => setFormData({...formData, sender_name: e.target.value})}
            data-testid="contact-name"
          />
        </div>
        <div>
          <Label>Your Phone</Label>
          <Input 
            placeholder="Phone number (optional)"
            value={formData.sender_phone}
            onChange={(e) => setFormData({...formData, sender_phone: e.target.value})}
          />
        </div>
      </div>
      
      <div>
        <Label>Your Email *</Label>
        <Input 
          type="email"
          placeholder="email@example.com"
          value={formData.sender_email}
          onChange={(e) => setFormData({...formData, sender_email: e.target.value})}
          data-testid="contact-email"
        />
        <p className="text-xs text-gray-500 mt-1">The post owner will reply to this email</p>
      </div>
      
      <div>
        <Label>Message *</Label>
        <Textarea 
          placeholder="Write your message here... Be specific about what you're interested in."
          value={formData.message}
          onChange={(e) => setFormData({...formData, message: e.target.value})}
          rows={4}
          data-testid="contact-message"
        />
      </div>
      
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <Alert>
        <AlertDescription className="text-sm">
          Your contact info will only be shared with the post owner. We recommend communicating through the platform initially.
        </AlertDescription>
      </Alert>
      
      <Button type="submit" className="w-full" disabled={loading} data-testid="send-message-btn">
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Send Message
          </>
        )}
      </Button>
    </form>
  );
};

// Message Card Component
const MessageCard = ({ message, onMarkRead }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <Card className={`mb-3 ${!message.read ? 'border-l-4 border-l-blue-500' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <User className="w-4 h-4 text-gray-400" />
              <span className="font-medium">{message.sender_name}</span>
              {!message.read && (
                <Badge className="bg-blue-500 text-xs">New</Badge>
              )}
            </div>
            
            <p className="text-sm text-gray-500 mb-2">
              Re: {message.post_title}
            </p>
            
            <p className={`text-sm ${expanded ? '' : 'line-clamp-2'}`}>
              {message.message}
            </p>
            
            {message.message.length > 100 && (
              <Button 
                variant="link" 
                size="sm" 
                onClick={() => setExpanded(!expanded)}
                className="p-0 h-auto text-blue-600"
              >
                {expanded ? 'Show less' : 'Read more'}
              </Button>
            )}
            
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {message.sender_email}
              </span>
              {message.sender_phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {message.sender_phone}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(message.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => window.location.href = `mailto:${message.sender_email}?subject=Re: ${message.post_title}`}
            >
              <SendHorizontal className="w-4 h-4 mr-1" />
              Reply
            </Button>
            {!message.read && onMarkRead && (
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => onMarkRead(message.id)}
              >
                <Eye className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Inbox Component
export const CommunityInbox = ({ userId }) => {
  const { country } = useCountry();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  
  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/community/messages/inbox?user_id=${userId}&country=${country}`);
      const data = await response.json();
      setMessages(data.messages || []);
      setStats({ total: data.total, unread: data.unread });
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (userId) {
      fetchMessages();
    }
  }, [userId, country]);
  
  const handleMarkRead = async (messageId) => {
    // Mark as read (would need backend endpoint)
    setMessages(prev => prev.map(m => 
      m.id === messageId ? {...m, read: true} : m
    ));
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  return (
    <div>
      {/* Stats */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold">Community Messages</h2>
          <p className="text-gray-500">
            {stats.total} messages, {stats.unread} unread
          </p>
        </div>
        <Button onClick={fetchMessages} variant="outline" size="sm">
          Refresh
        </Button>
      </div>
      
      {/* Messages List */}
      {messages.length > 0 ? (
        messages.map(message => (
          <MessageCard 
            key={message.id} 
            message={message} 
            onMarkRead={handleMarkRead}
          />
        ))
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Inbox className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium mb-2">No Messages Yet</h3>
            <p className="text-gray-500">
              When someone contacts you about your community posts, messages will appear here.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Contact Page (standalone)
export default function CommunityContactPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { country } = useCountry();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(`${API_URL}/api/community/posts/${postId}?country=${country}`);
        const data = await response.json();
        setPost(data);
      } catch (error) {
        console.error('Failed to fetch post:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (postId) {
      fetchPost();
    }
  }, [postId, country]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-xl font-bold mb-4">Post Not Found</h2>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle>Contact Poster</CardTitle>
                <CardDescription>Send a message about this listing</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ContactPostOwnerForm 
              post={post} 
              onSuccess={() => {
                setTimeout(() => navigate(-1), 2000);
              }}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Export the contact form for use in modals
export { ContactPostOwnerForm };
